import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useContact } from '../../context/ContactContext';
import { useCallTimer } from '../../hooks/useCallTimer';
import { useCallbacks } from '../../hooks/useCallbacks';
import TransferPanel from './TransferPanel';
import { railTab } from '../../lib/railTab';
import { statusLocked, STATUS_LOCKED_NOTE } from '../../lib/wrapUpComplete';
import { statusOptions, needsNote, validateNote, pillClassFor, NOTE_MAX, formatDuration, statusStartMs, STATUS_LIMIT_MIN } from '../../lib/agentStatuses';
// v3 design parity (#112, epic #117): both rails, scoped to .v3-rail.
import '../../styles/rails-v3.css';

export default function LeftRail() {
  const { agent, wrapContext, agentStatus, agentStatusPending, agentStatusError,
          agentConnected, callActive, dialling, contact, isMuted, isHeld, display, isDemo,
          activeTab, setActiveTab, stuckContacts, incomingContact,
          transferPanelOpen, setTransferPanelOpen,
          handleMute, handleHold, handleEnd, handleSetAgentState, handleClearStuck } = useContact();
  const { formatted: callTime } = useCallTimer(callActive);
  // #113 review (2026-09-22): while a contact rings, the call controls are
  // disabled, as they effectively were before the ring screen moved inside
  // the shell (the rail did not render then). This matters when a second
  // contact rings during a connected call: CONTACT_ENDED clears
  // incomingContact whatever contact ended, so End here would take the ring
  // card and ringtone away from a contact still ringing in Connect. Accept
  // or Decline it on the card first.
  const ringing = !!incomingContact;
  const { dueCount, overdue } = useCallbacks(agentConnected);

  // A live contact owns the centre; highlight the tab matching its channel.
  // The channel lives at display.meta.channel (uppercase, set by
  // displayFromContact), the same field CallbackTaskCard reads. Review finding
  // 2026-09-22: `display.channel` never existed, so this was always 'voice'.
  const isTask = display?.meta?.channel === 'TASK';
  // A ringing contact (no display yet) owns the highlight too: railTab().
  const currentTab = railTab({ display, incomingContact, activeTab });

  const aiMins = Math.floor((display?.aiHandledSeconds ?? 0) / 60);
  const aiSecs = (display?.aiHandledSeconds ?? 0) % 60;
  const aiTime = `${String(aiMins).padStart(2,'0')}:${String(aiSecs).padStart(2,'0')}`;

  // D6/D19: a missed/error contact holds the agent non-routable on every
  // channel. Say so, instead of "Available".
  const stuck = !callActive && (stuckContacts?.length ?? 0) > 0;
  // D27: while a request is in flight the select shows what was asked for, so
  // it no longer snaps back to the old value before Connect answers. The pill
  // colour stays on agentStatus — that is still the truth until confirmed.
  const shownStatus = agentStatusPending ?? agentStatus;
  // Row 200: the options come from Connect (Available, Break, Lunch, Meeting,
  // Training, Offline), falling back to Available/Offline if unreadable.
  let liveStates = [];
  try { liveStates = agent?.getAgentStates?.() ?? []; } catch { liveStates = []; }
  const options = statusOptions(liveStates);
  const selectValue = options.includes(shownStatus) ? shownStatus : '';

  // Row 200: time in the current status on the pill. Streams' own start time
  // when readable, else when this console saw the change; ticks once a second.
  const sinceRef = React.useRef({ status: null, at: null });
  if (sinceRef.current.status !== agentStatus) sinceRef.current = { status: agentStatus, at: Date.now() };
  const [nowTick, setNowTick] = React.useState(Date.now());
  React.useEffect(() => {
    if (callActive) return undefined;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [callActive]);
  const startedAt = statusStartMs(agent, sinceRef.current.at);
  // No timer on Offline (Storm 2026-10-22): Connect keeps Offline's start from
  // the last sign-out, so it read e.g. 17:46:39 on signing in the next day.
  const timeInStatus = startedAt && agentStatus !== 'Offline' ? formatDuration(nowTick - startedAt) : null;
  // Away statuses change colour with time in status (lib/agentStatuses.js).
  const pillClass = callActive ? 'active' : pillClassFor(agentStatus, startedAt ? nowTick - startedAt : 0);
  const limitMin = STATUS_LIMIT_MIN[agentStatus];
  // Meeting and Training ask for a short note before the status is set.
  const [noteFor, setNoteFor] = React.useState(null);
  const [noteText, setNoteText] = React.useState('');
  const [noteError, setNoteError] = React.useState(null);
  // 22 Oct: no status changes while a wrap-up is open (see lib/wrapUpComplete.js).
  const locked = statusLocked({ wrapContext });
  const pickStatus = name => {
    if (locked) return;
    if (needsNote(name)) { setNoteFor(name); setNoteText(''); setNoteError(null); return; }
    setNoteFor(null);
    handleSetAgentState(name);
  };
  const submitNote = e => {
    e.preventDefault();
    const v = validateNote(noteText);
    if (!v.ok) { setNoteError(v.error); return; }
    handleSetAgentState(noteFor, v.note);
    setNoteFor(null); setNoteText(''); setNoteError(null);
  };

  return (
    <aside className="left-rail v3-rail">
      {/* The wordmark moved to the console header (#113, 2026-09-22): the
          header is now the element on every screen (vault #187's intent). */}

      {/* Channel navigation */}
      <nav className="lr-tabs" aria-label="Workspace">
        <button
          className={`lr-tab${currentTab === 'voice' ? ' active' : ''}`}
          onClick={() => setActiveTab('voice')}
          title="Voice workspace"
        >
          <span className="lr-tab-dot" aria-hidden="true" />
          <span>Voice</span>
          {callActive && <span className="tab-live">Live</span>}
        </button>
        <button
          className={`lr-tab${currentTab === 'chat' ? ' active' : ''}`}
          onClick={() => setActiveTab('chat')}
          title="Chat — coming soon"
        >
          <span className="lr-tab-dot" aria-hidden="true" />
          <span>Chat</span>
          <span className="tab-soon">Soon</span>
        </button>
        <button
          className={`lr-tab${currentTab === 'task' ? ' active' : ''}`}
          onClick={() => setActiveTab('task')}
          title="Task queue"
        >
          <span className="lr-tab-dot" aria-hidden="true" />
          <span>Task</span>
          {dueCount > 0 && (
            <span className={`tab-badge${overdue ? ' overdue' : ''}`}>{dueCount}</span>
          )}
        </button>
      </nav>

      {/* Status */}
      <div className="lr-section">
        {incomingContact ? (
          // #113: the ringing screen now renders inside the shell, so the rail
          // says what is happening (spec: "Incoming"), same colour as on-call.
          // Checked before callActive on purpose (review, 2026-09-22): a
          // second contact can ring while one is connected (a callback TASK
          // during a voice call, cross-channel routing), and the centre shows
          // the ring card then, so the pill must say so too.
          <span className="status-pill active incoming">
            <span className="status-dot" />
            Incoming
          </span>
        ) : isDemo || callActive || dialling ? (
          <span className="status-pill active">
            <span className="status-dot" />
            {callActive
              ? (contact?.getType() === 'task' ? 'Active Task' : 'Active Call')
              : dialling ? 'Dialling…' : (agentStatus ?? 'Available')}
          </span>
        ) : stuck ? (
          <span className="status-pill stuck" title="A missed contact is holding your session">
            <span className="status-dot" />
            Not routable
          </span>
        ) : (
          <div className={`status-pill ${pillClass} status-select-wrap`}>
            <span className="status-dot" />
            <select
              className="status-select"
              value={selectValue}
              disabled={!!agentStatusPending || locked}
              onChange={e => pickStatus(e.target.value)}
              title={locked ? STATUS_LOCKED_NOTE : agentStatusPending ? `Waiting for Connect to confirm ${agentStatusPending}` : 'Set your status'}
            >
              {selectValue === '' && (
                <option value="" disabled>{shownStatus ?? 'Available'}</option>
              )}
              {options.map(name => (
                <option key={name} value={name}>{agentStatusPending === name ? `Setting ${name}…` : name}</option>
              ))}
            </select>
            {timeInStatus && !agentStatusPending && (
              <span className="status-time" title={limitMin ? `Time in this status (limit ${limitMin} min)` : 'Time in this status'}>{timeInStatus}</span>
            )}
          </div>
        )}
        {locked && !callActive && !incomingContact && (
          <div className="status-locked-note">{STATUS_LOCKED_NOTE}</div>
        )}
        {/* Row 200: required short note for Meeting / Training */}
        {noteFor && !callActive && !incomingContact && (
          <form className="status-note" onSubmit={submitNote}>
            <label htmlFor="status-note-input">{noteFor}: what is it for?</label>
            <input
              id="status-note-input"
              type="text"
              maxLength={NOTE_MAX}
              value={noteText}
              autoFocus
              onChange={e => { setNoteText(e.target.value); setNoteError(null); }}
              placeholder="e.g. team huddle, product training"
            />
            {noteError && <div className="status-note-error" role="alert">{noteError}</div>}
            <div className="status-note-actions">
              <button type="submit">Set {noteFor}</button>
              <button type="button" onClick={() => { setNoteFor(null); setNoteError(null); }}>Cancel</button>
            </div>
          </form>
        )}
        {/* D27: a refused or unconfirmed state change, beside the pill */}
        {agentStatusError && !callActive && (
          <div className="status-error" role="alert">
            <AlertTriangle size={12} />
            <span>{agentStatusError}</span>
          </div>
        )}
      </div>

      {/* D6/D19: stuck-contact banner. D32 (2026-09-14): an ENDED contact
          whose wrap-up is unsaved past the threshold is listed too, with NO
          clear control — save it on the wrap-up screen. */}
      {stuck && (() => {
        const missed = stuckContacts.filter(s => s.status !== 'ended');
        const unsaved = stuckContacts.filter(s => s.status === 'ended');
        const head = missed.length && unsaved.length
          ? 'Missed contacts and an unsaved wrap-up are blocking new contacts'
          : unsaved.length
            ? 'An unsaved wrap-up is blocking new contacts'
            : missed.length === 1 ? 'A missed contact is blocking new contacts'
              : `${missed.length} missed contacts are blocking new contacts`;
        return (
          <div className="stuck-banner" role="alert">
            <div className="stuck-banner-head">
              <AlertTriangle size={14} />
              <strong>{head}</strong>
            </div>
            {missed.length > 0 && (
              <p>Connect holds your session until it is cleared. Clearing re-offers it to you.</p>
            )}
            {missed.map(s => (
              <button
                key={s.id}
                className="stuck-clear-btn"
                onClick={() => handleClearStuck(s.id)}
                title={`Clear ${s.status} ${s.type} ${s.id}`}
              >
                Clear {s.status} {s.type}
              </button>
            ))}
            {unsaved.length > 0 && (
              <p className="stuck-acw-note">
                <strong>No calls, tasks or chats can reach you</strong> until the wrap-up is saved.
                Open the wrap-up screen and press Save &amp; Complete — it cannot be cleared from here.
              </p>
            )}
          </div>
        );
      })()}

      {/* Timers */}
      <div className="lr-section lr-duration">
        <span className="lr-label">Duration</span>
        <div className="timer-block">
          <div className="timer-row">
            <span className="timer-name">Call</span>
            <span className="timer-value">{callActive ? callTime : '00:00'}</span>
          </div>
          <div className="timer-row">
            <span className="timer-name">AI handled</span>
            <span className="timer-value ai-timer">
              {display?.aiHandled ? aiTime : '—'}
            </span>
          </div>
        </div>
      </div>

      {/* Live sentiment moved to the right rail, directly above Coaching
          (#112, Storm 2026-09-22, vault #222): coaching trigger 2 fires off
          the same live feed the sparkline draws, so the two sit together. */}

      {/* Call Controls */}
      <div className="lr-section lr-controls">
        <span className="lr-label">Controls</span>
        <div className="controls-grid">
          <button
            className={`ctrl-btn ${isMuted ? 'on' : ''}`}
            onClick={handleMute}
            disabled={!callActive || ringing}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? 'Unmute' : 'Mute'}
          </button>

          <button
            className={`ctrl-btn ${isHeld ? 'on' : ''}`}
            onClick={handleHold}
            disabled={!callActive || ringing}
            title={isHeld ? 'Resume' : 'Hold'}
          >
            {isHeld ? 'Resume' : 'Hold'}
          </button>

          {/* Row 1 Transfer, Phase 1 (2026-09-28): opens the picker directly
              below this grid (TransferPanel.jsx, left rail since 2026-09-29) — same disabled condition as
              Hold, a live connected leg is required and a second ringing
              contact must be handled first. */}
          <button
            className={`ctrl-btn ${transferPanelOpen ? 'on' : ''}`}
            onClick={() => setTransferPanelOpen(open => !open)}
            disabled={!callActive || ringing}
            title="Transfer"
          >
            Transfer
          </button>

          {/* D37 (2026-09-16): End works while an outbound call is still
              ringing (dialling) — the only control that does; Mute/Hold need
              a connected leg. See lib/callState.js controlsFor().
              #112 (Storm 2026-09-22): End sits in the same 2x2 grid as the
              other three, same size, not full width underneath. */}
          <button
            className="ctrl-btn end"
            onClick={handleEnd}
            disabled={!(callActive || dialling) || ringing}
            title={dialling ? 'Hang up (ringing)' : isTask ? 'End task' : 'End call'}
          >
            {isTask ? 'End task' : 'End call'}
          </button>
        </div>
        {/* Transfer options open here, under the Transfer button (Storm
            2026-09-29). Renders nothing while closed. */}
        <TransferPanel />
      </div>

      {/* Rail foot, pinned to the bottom (spec: margin-top auto). */}
      <div className="lr-foot">
        {/* #112 (Storm 2026-09-21): the tenant on a live contact, from the
            contact's own brandName. Outbound and callback-task calls showed it
            nowhere during the call. Omitted when idle: a multi-tenant agent has
            no single honest value there. */}
        {(callActive || dialling) && display?.brandName && (
          <div className="lr-section lr-tenant">
            <span className="lr-label">Tenant</span>
            <div className="lr-tenant-name">{display.brandName}</div>
          </div>
        )}

        {/* Console identity + sign-out left the rail for the header's avatar
            menu (#113, 2026-09-22), in the same change, so no build is ever
            without a visible sign-out. */}
      </div>

    </aside>
  );
}
